import { updateCreditsDisplay } from './credits';

const conversationHistory = [];

// The transcript only reaches the server when the consultation is finished, so
// until then it is kept in sessionStorage. That way leaving the page — the Back
// button, a stray reload — and coming back rejoins the consultation where it
// was rather than starting the patient over. sessionStorage rather than
// localStorage: it lasts as long as the tab, so a consultation abandoned days
// ago does not reappear.
let restoringTranscript = false;

function transcriptKey() {
  const chatMessages = document.getElementById('chat-messages');
  return chatMessages ? `starfish:consultation:${chatMessages.dataset.consultationId}` : null;
}

function bubbleRole(bubble) {
  if (bubble.classList.contains('user')) return 'user';
  if (bubble.classList.contains('examination')) return 'examination';
  return 'assistant';
}

function saveTranscript() {
  const key = transcriptKey();
  if (!key || restoringTranscript) return;

  const bubbles = Array.from(document.querySelectorAll('#chat-messages .chat-bubble')).map(function(b) {
    return { role: bubbleRole(b), text: b.textContent };
  });

  // Private browsing and full quotas both throw here. Losing the ability to
  // rejoin is not worth breaking the consultation over.
  try {
    if (bubbles.length) {
      sessionStorage.setItem(key, JSON.stringify({ bubbles: bubbles, history: conversationHistory }));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (err) {
    console.warn('Could not save consultation progress:', err);
  }
}

function clearTranscript() {
  const key = transcriptKey();
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch (err) {
    console.warn('Could not clear consultation progress:', err);
  }
}

function restoreTranscript() {
  const key = transcriptKey();
  if (!key) return false;

  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(key) || 'null');
  } catch (err) {
    saved = null;
  }
  if (!saved || !Array.isArray(saved.bubbles) || !saved.bubbles.length) return false;

  restoringTranscript = true;
  saved.bubbles.forEach(function(b) { appendBubble(b.text, b.role); });
  (saved.history || []).forEach(function(m) { conversationHistory.push(m); });
  restoringTranscript = false;
  return true;
}

function startOver() {
  clearTranscript();
  conversationHistory.length = 0;
  const chatMessages = document.getElementById('chat-messages');
  if (chatMessages) chatMessages.innerHTML = '';
  const notice = document.getElementById('rejoin-notice');
  if (notice) notice.classList.add('d-none');
  const input = document.getElementById('message-input');
  if (input) input.focus();
}

function saveNote(sessionId, url) {
  const notes = document.getElementById('note-' + sessionId).value;
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

  fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify({ notes: notes })
  })
  .then(response => response.json())
  .then(data => {
    if (data.success) {
      const indicator = document.getElementById('saved-' + sessionId);
      indicator.classList.remove('d-none');
      setTimeout(() => { indicator.classList.add('d-none'); }, 2000);
    }
  })
  .catch(err => console.error('Save note error:', err));
}

function appendBubble(text, role) {
  const bubble = document.createElement('div');
  bubble.className = 'chat-bubble ' + role;
  bubble.textContent = text;
  const chatMessages = document.getElementById('chat-messages');
  chatMessages.appendChild(bubble);
  chatMessages.scrollTop = chatMessages.scrollHeight;
  saveTranscript();
}

function setInputEnabled(enabled) {
  const input = document.getElementById('message-input');
  const button = document.querySelector('#chat-form button[type="submit"]');
  input.disabled = !enabled;
  button.disabled = !enabled;
}

function showNoCreditsWarning() {
  const banner = document.getElementById('no-credits-banner');
  if (banner) banner.classList.remove('d-none');
  setInputEnabled(false);
}

function sendUserMessage(event) {
  event.preventDefault();
  const input = document.getElementById('message-input');
  const text = input.value.trim();
  if (!text) return;

  appendBubble(text, 'user');
  conversationHistory.push({ role: 'user', content: text });
  saveTranscript();
  input.value = '';
  setInputEnabled(false);

  const consultationId = document.getElementById('chat-messages').dataset.consultationId;
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

  fetch(`/consultations/${consultationId}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify({ messages: conversationHistory })
  })
  .then(response => response.json())
  .then(data => {
    if (data.error === 'out_of_credits') {
      showNoCreditsWarning();
      return;
    }
    if (data.response) {
      appendBubble(data.response, 'assistant');
      conversationHistory.push({ role: 'assistant', content: data.response });
      saveTranscript();
    } else if (data.error) {
      appendBubble('Error: ' + data.error, 'assistant');
    }
    updateCreditsDisplay(data.credits_remaining);
    if (data.credits_remaining != null && data.credits_remaining <= 0) {
      showNoCreditsWarning();
    } else {
      setInputEnabled(true);
      input.focus();
    }
  })
  .catch(err => {
    console.error('Fetch error:', err);
    appendBubble('Something went wrong. Please try again.', 'assistant');
    setInputEnabled(true);
  });
}

function finishConsultation() {
  const consultationId = document.getElementById('chat-messages').dataset.consultationId;
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

  return fetch(`/consultations/${consultationId}/finish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify({
      messages: conversationHistory
    })
  })
  .then(response => response.json())
  .then(data => {
    // The server has the transcript now, so there is nothing left to rejoin.
    if (data.redirect_url) {
      clearTranscript();
      window.location.href = data.redirect_url;
    }
  })
  .catch(() => alert('Could not save consultation. Please try again.'));
}

function initConsultation() {
  const chatMessages = document.getElementById('chat-messages');

  // Guarded so a second init — turbo:load and DOMContentLoaded both firing —
  // cannot replay the saved transcript on top of itself.
  if (chatMessages && !chatMessages.dataset.rejoinChecked) {
    chatMessages.dataset.rejoinChecked = 'true';
    conversationHistory.length = 0;

    if (restoreTranscript()) {
      const notice = document.getElementById('rejoin-notice');
      if (notice) notice.classList.remove('d-none');
    }
  }

  const startOverBtn = document.getElementById('btn-start-over');
  if (startOverBtn) startOverBtn.addEventListener('click', startOver);

  if (chatMessages && chatMessages.dataset.creditsRemaining !== '' &&
      chatMessages.dataset.creditsRemaining !== 'null' &&
      parseInt(chatMessages.dataset.creditsRemaining, 10) <= 0) {
    showNoCreditsWarning();
  }

  const form = document.getElementById('chat-form');
  if (form) form.addEventListener('submit', sendUserMessage);

  // Enter sends and never lands in the text; long lines wrap rather than break.
  const messageInput = document.getElementById('message-input');
  if (messageInput) {
    // Ready to type as soon as the consultation opens.
    messageInput.focus();
    messageInput.addEventListener('keydown', function(event) {
      if (event.key === 'Enter' && !event.isComposing) {
        event.preventDefault();
        form.requestSubmit();
      }
    });
  }

  const finishBtn = document.getElementById('finish-now-btn');
  if (finishBtn) {
    finishBtn.addEventListener('click', function() {
      finishBtn.disabled = true;
      finishConsultation().finally(function() { finishBtn.disabled = false; });
    });
  }

  document.querySelectorAll('.js-examine-option').forEach(function(btn) {
    btn.addEventListener('click', function() {
      appendBubble(btn.dataset.name + ': ' + btn.dataset.finding, 'examination');
    });
  });

  document.querySelectorAll('.js-save-note').forEach(function(btn) {
    btn.addEventListener('click', function() {
      saveNote(btn.dataset.sessionId, btn.dataset.url);
    });
  });

  document.querySelectorAll('.js-delete-session').forEach(function(btn) {
    btn.addEventListener('click', function() {
      if (!confirm('Delete this consultation? This cannot be undone.')) return;
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
      fetch(btn.dataset.url, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) btn.closest('.accordion-item').remove();
      })
      .catch(err => console.error('Delete error:', err));
    });
  });
}

document.addEventListener('turbo:load', initConsultation);
document.addEventListener('DOMContentLoaded', initConsultation);
